import * as S from "./EquipDataContent.style";
import { ColDef, GridOptions } from "ag-grid-community";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Buttons } from "../../../atoms/Buttons";
import { HeatmapCharts } from "../../../atoms/Charts/HeatmapChart";
import { Grid } from "../../../molecules/Grid";
import { SearchBar } from "../../../molecules/SearchBar";
import { SideTabList } from "../../../molecules/SideTabList";
import { ContentGridItem } from "../../../molecules/ContentGridItem";
import { ModalAlarmChart } from "../../../molecules/Modals/ModalAlarmChart";
import { useQuery } from "@tanstack/react-query";

import { request } from "../../../../common/api";
import {
  EquipmentDefaultValuesTypes,
  PageCurrentShipTypes,
} from "../../../../types/equipment";
import { Loading } from "../../../atoms/Loading";
import { validateDateRange } from "../../../../common/functions/validateFunctions";
import {
  GET_ALARM_HEATMAP_LIST,
  GET_ALARM_STATISTIC_CHART_DATA,
  GET_STATUS_DIAGNOSIS_DATA_LIST,
} from "../../../../common/query-keys";
import { ExplainText } from "../../../atoms/Charts/ExplainText";
import dayjs from "dayjs";
import { InputDateHour } from "../../../atoms/Inputs/InputDateHour";

interface EquipDataContentProps {
  search?: EquipmentDefaultValuesTypes;
  handleDateChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  localCurrentShip?: PageCurrentShipTypes;
  setSearchData?: (key: string, value: any) => void;
  setSearch: React.Dispatch<React.SetStateAction<EquipmentDefaultValuesTypes>>;
}

export interface EquipDataResponse {
  steLowMcyName: string;
  timeslots: Date[];
  counts: number[];
  statisticsTable: StatisticsTable;
}

export interface StatisticsTable {
  unknown: number;
  normal: number;
  warning: number;
  critical: number;
  total: number;
}

export const EquipDataContent = ({
  search,
  localCurrentShip,
  setSearch,
}: EquipDataContentProps) => {
  const [openTabId, setOpenTabId] = useState<string | null>(null); // 현재 열려 있는 탭 ID 관리

  const [modalShow, setModalShow] = useState(false);
  // 조회 조건의 돋보기( 검색 )버튼을 눌렀을 때 search 변수에 있는 값을 searchConfirm에 저장하면서 useQuery select 트리거
  const [searchConfirm, setSearchConfirm] =
    useState<EquipmentDefaultValuesTypes>(search ?? {});

  /** 장비모니터링> 상태진단 데이터조회 > 하위장비/센서 목록
   * * fetch 코드 수정 완료 2025.05.23
   */
  const { data: statusDiagnosisDataList, isLoading } = useQuery({
    queryKey: [GET_STATUS_DIAGNOSIS_DATA_LIST],
    queryFn: async () => {
      return await request<any>({
        method: "GET",
        url: `/equipment-monitoring/status-diagnostic-mcy-list`,
        params: {
          steShiptypeNo: searchConfirm.shipTypeNo,
          steShipNo: searchConfirm.shipNo,
          steMcyName: searchConfirm.mcyName,
        },
      }).then((res: any) => {
        return res.data;
      });
    },
  });

  /** 장비모니터링> 상태진단 데이터조회 > 센서별 알람 발생 히트맵
   * * fetch 코드 수정 완료 2025.05.23
   */
  const {
    data: equipDataList,
    isFetching: equipDataListIsLoading,
    refetch: alarmHeatMapRefetch,
  } = useQuery({
    queryKey: [GET_ALARM_HEATMAP_LIST, searchConfirm],
    queryFn: async () => {
      return await request<any>({
        method: "GET",
        url: `/equipment-monitoring/alarm-heat-map`,
        params: {
          steShiptypeNo: searchConfirm?.shipTypeNo,
          steShipNo: searchConfirm?.shipNo,
          steMcyName: searchConfirm?.mcyName,
          startDate: searchConfirm?.startDate,
          endDate: searchConfirm?.endDate,
          steLowMcyName: searchConfirm?.lowMcyName || null, // 계통 드롭다운 클릭 시
        },
      }).then((res: any) => {
        return res.data;
      });
    },
    enabled: !!searchConfirm?.startDate && !!searchConfirm?.endDate,
  });

  /** [ 모달 ] 장비모니터링> 상태진단 데이터조회 > 센서 상태진단 알람통계 차트
   * * fetch 코드 수정 완료 2025.05.23
   */
  const { data: alarmStatisticsChartData } = useQuery({
    queryKey: [GET_ALARM_STATISTIC_CHART_DATA, searchConfirm],
    queryFn: async () => {
      return await request<any>({
        method: "GET",
        url: `/equipment-monitoring/alarm-statistics-chart`,
        params: {
          steShiptypeNo: searchConfirm?.shipTypeNo,
          steShipNo: searchConfirm?.shipNo,
          steMcyName: searchConfirm?.mcyName,
          startDate: searchConfirm?.startDate,
          endDate: searchConfirm?.endDate,
          steLowMcyName: searchConfirm?.lowMcyName,
          steSensorCode: searchConfirm?.sensorCode,
        },
      }).then((res: any) => {
        return res.data;
      });
    },
    enabled: !!searchConfirm?.sensorCode,
  });

  /** 계통/센서 목록에서 계통 클릭 시 핸들러 */
  const onClickSubmenu = (lowMcyName: string) => {
    // setSearchData?.("lowMcyName", lowMcyName);
    setSearch({ ...search, lowMcyName: lowMcyName });
    setSearchConfirm({ ...search, lowMcyName: lowMcyName });
  };

  /** 히트맵 클릭 이벤트 함수  */
  const onClickHeatMap = (sensorCode: string | null) => {
    setSearchConfirm({ ...search, sensorCode: sensorCode });
    // setSearchData?.("sensorCode", sensorCode);
    setSearch({ ...search, sensorCode: sensorCode });
    setModalShow(!modalShow);
  };

  // 초기화 버튼 클릭 핸들러
  const handleLowMcyNameChange = useCallback(() => {
    setOpenTabId(null); // 모든 탭 닫기
    // setSearchData?.("lowMcyName", null);
    setSearch({ ...search, lowMcyName: null });
    setSearchConfirm({ ...search, lowMcyName: null });
  }, [search, setSearch]);

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // 날짜 유효성 검사
    const result = validateDateRange(search?.startDate, search?.endDate, 90);

    if (!result) {
      return;
    }

    setSearchConfirm(search ?? {});
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: `${equipDataList?.type === "mcy" ? "계통" : "센서"}명`,
        field: "steLowMcyOrSensorName",
        width: 220,
        cellStyle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        cellRenderer: (params: any) => {
          const value = params.value;
          return value && value.length > 30
            ? `${value.slice(0, 30)}...`
            : value;
        },
      },
      {
        headerName: "정상",
        field: "normal",
        flex: 1,
      },

      {
        headerName: "이상",
        field: "warning",
        flex: 1,
      },

      {
        headerName: "경고",
        field: "critical",
        flex: 1,
      },

      {
        headerName: "진단불가",
        field: "unknown",
        flex: 1,
      },

      {
        headerName: "Total",
        field: "total",
        flex: 1,
      },
    ],
    [equipDataList?.type]
  );

  // 그리드 옵션 설정
  const gridOptions: GridOptions = {
    rowHeight: 40, // 원하는 높이로 수정
    headerHeight: 30, // 헤더 높이 설정
  };

  /** 상태진단 페이지 날짜 변경 핸들러 함수 */
  const analysisDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const startDate = dayjs(e.target.value).format("YYYY-MM-DD HH:mm:ss");
    const endDate = dayjs(e.target.value)
      .add(90, "days")
      .format("YYYY-MM-DD HH:mm:ss");

    setSearch({
      ...searchConfirm,
      startDate: startDate,
      endDate: endDate,
    });
  };

  useEffect(() => {
    setSearchConfirm(search ?? {});
  }, [localCurrentShip]);

  useEffect(() => {
    if (searchConfirm.lowMcyName) {
      const selectedTab = statusDiagnosisDataList?.find(
        (tabData: { id: number; title: string; depth: number; submenu: any }) =>
          tabData.title === searchConfirm.lowMcyName
      );
      setOpenTabId(selectedTab?.id ?? null); // id가 없으면 null로 설정
    }
  }, [searchConfirm.lowMcyName, statusDiagnosisDataList, isLoading]);

  return (
    <>
      <S.EquipDataContent>
        <S.EquipDataTopbar>
          <SearchBar
            onSearch={handleSearch}
            searchInput={
              <>
                <InputDateHour
                  size="md"
                  startName="startDate"
                  onChange={analysisDateChange}
                  defaultValue={search?.startDate}
                  value={search?.startDate}
                />
                <p>~</p>
                <InputDateHour
                  size="md"
                  startName="endDate"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setSearch({
                      ...search,
                      endDate: dayjs(e.target.value).format(
                        "YYYY-MM-DD HH:mm:ss"
                      ),
                    });
                  }}
                  defaultValue={search?.endDate}
                  value={search?.endDate}
                />
                <Buttons
                  type="button"
                  size="md"
                  layout="selectCondition"
                  label="전3개월"
                  onClick={() => {
                    const startDate = dayjs()
                      .subtract(90, "days")
                      .format("YYYY-MM-DD 00:00:00");
                    const endDate = dayjs().format("YYYY-MM-DD 23:59:59");

                    setSearch({
                      ...search,
                      startDate: startDate,
                      endDate: endDate,
                    });

                    // 검색전송 트리거 상태값 변경
                    setSearchConfirm?.({
                      ...search,
                      startDate: startDate,
                      endDate: endDate,
                    });
                  }}
                />
              </>
            }
          />

          <ExplainText>
            <>
              <span className="bold">[안내] </span>
              시스템 성능을 고려하여 히트맵은 계통 단위로 조회를 권고합니다.
              <br />
              센서 단위로 조회하는 경우에 정상적으로 표시가 안될 수 있습니다.
            </>
          </ExplainText>
        </S.EquipDataTopbar>

        <S.EquipDataBox>
          {/* 하위장비/센서 목록 */}
          <ContentGridItem
            title={
              <>
                계통/센서 목록
                <Buttons
                  type="button"
                  size="sm"
                  layout="primary"
                  label="초기화"
                  onClick={handleLowMcyNameChange}
                />
              </>
            }
            underline={true}
            tabList={
              <SideTabList
                tabList={statusDiagnosisDataList || []}
                onClickSub={onClickSubmenu}
                openTabId={openTabId} // 현재 열려 있는 탭 ID 전달
                setOpenTabId={setOpenTabId} // 탭 상태 변경 함수 전달
                hoverUse={false}
              />
            }
          />

          {/* 센서별 알람 발생 히트맵 */}
          <ContentGridItem
            title={
              <>
                계통/센서 알람 발생 히트맵
                {/* <Buttons
                  type="button"
                  size="sm"
                  layout="primary"
                  label="계통 히트맵 보기"
                /> */}
              </>
            }
            underline={true}
          >
            {equipDataListIsLoading ? (
              <Loading />
            ) : (
              <>
                {/* 불러온 데이터의 길이가 0이면 조회된 데이터가 없다는 문구를
                출력하고, 데이터가 있으면 HeatmapCharts 컴포넌트를 렌더링합니다. */}
                {equipDataList?.result ? (
                  equipDataList?.result?.map((item: any) => {
                    return (
                      <HeatmapCharts
                        label={`${item.steLowMcyName}`}
                        data={item}
                        onClickHeatMap={search?.lowMcyName && onClickHeatMap}
                      />
                    );
                  })
                ) : (
                  <S.EquipDescriptionText>
                    조회된 데이터가 없습니다.
                  </S.EquipDescriptionText>
                )}
              </>
            )}
          </ContentGridItem>

          {/* 하위장비/센서별 상태진단 알람 발생 통계 */}
          <ContentGridItem
            title={<>계통/센서 상태진단 알람 발생 통계</>}
            underline={false}
          >
            <Grid
              rowData={
                equipDataList?.result?.map((item: EquipDataResponse) => {
                  return {
                    steLowMcyOrSensorName: item.steLowMcyName,
                    normal: item.statisticsTable.normal,
                    warning: item.statisticsTable.warning,
                    critical: item.statisticsTable.critical,
                    unknown: item.statisticsTable.unknown,
                    total: item.statisticsTable.total,
                  };
                }) || []
              }
              columnDefs={columnDefs}
              // pagingShow={true}
              pagingShow={false}
              gridOptions={gridOptions}
              defaultPageSize={30}
            />
          </ContentGridItem>
        </S.EquipDataBox>
      </S.EquipDataContent>
      {modalShow && (
        <ModalAlarmChart
          setModalShow={setModalShow}
          search={search}
          chartData={alarmStatisticsChartData || []}
        />
      )}
    </>
  );
};
